import { createId } from "@/lib/ids";
import type { OklchColor } from "./color";

export type AnimationNodeKind = "animation" | "group" | "animate" | "motion";

export type AnimationEasingName =
  | "linear"
  | "easeIn"
  | "easeOut"
  | "easeInOut"
  | "easeCirc"
  | "easeBack"
  | "easeBounce"
  | "easeElastic"
  | "overShoot";

export interface AnimationPresetEasing {
  kind: "preset";
  name: AnimationEasingName;
}

export interface AnimationWiggleEasing {
  kind: "wiggle";
  frequency: number;
  phase: number;
  amplitude: number;
  decay: number;
}

export type AnimationEasing = AnimationPresetEasing | AnimationWiggleEasing;

export type AnimationTransitionEffectName =
  | "cut"
  | "crossfade"
  | "slideLeft"
  | "slideRight"
  | "slideUp"
  | "slideDown"
  | "zoom"
  | "push"
  | "wipe"
  | "fadeThroughColor";

export const ANIMATION_TRANSITION_EFFECTS: Array<{ name: AnimationTransitionEffectName; label: string }> = [
  { name: "cut", label: "Cut" },
  { name: "crossfade", label: "Crossfade" },
  { name: "slideLeft", label: "Slide Left" },
  { name: "slideRight", label: "Slide Right" },
  { name: "slideUp", label: "Slide Up" },
  { name: "slideDown", label: "Slide Down" },
  { name: "zoom", label: "Zoom" },
  { name: "push", label: "Push" },
  { name: "wipe", label: "Wipe" },
  { name: "fadeThroughColor", label: "Fade through color" },
];

export const DEFAULT_TRANSITION_COLOR: OklchColor = { l: 0, c: 0, h: 0, alpha: 1 };

export type AnimationGroupRegroupMode = "none" | "words" | "symbols" | "symbolsWithoutWhitespace";

export interface AnimationGraph {
  nodes: AnimationGraphNode[];
  connections: AnimationConnection[];
  visualGroups?: AnimationGraphVisualGroup[];
}

export interface AnimationGraphVisualGroup {
  id: string;
  name: string;
  nodeIds: string[];
  collapsed: boolean;
}

export interface AnimationGraphPoint {
  x: number;
  y: number;
}

interface BaseAnimationNode {
  id: string;
  kind: AnimationNodeKind;
  name: string;
  position: AnimationGraphPoint;
  collapsed?: boolean;
}

export interface RootAnimationNode extends BaseAnimationNode {
  kind: "animation";
  frameId?: string;
}

export interface GroupAnimationNode extends BaseAnimationNode {
  kind: "group";
  targets: AnimationTarget[];
  regroup: AnimationGroupRegroupMode;
  regroupOrder?: string[];
}

export interface AnimateAnimationNode extends BaseAnimationNode {
  kind: "animate";
  target?: AnimationTarget;
  properties: AnimationProperty[];
}

export interface MotionAnimationNode extends BaseAnimationNode {
  kind: "motion";
  durationMs: number;
  instant: boolean;
  offsetMs: number;
  stutterMs: number;
  animateElementsIndependently?: boolean;
  easing: AnimationEasing;
  transition?: boolean;
  targetAnimationNodeId?: string;
  transitionEffect?: AnimationTransitionEffectName;
  transitionColor?: OklchColor;
}

export type AnimationGraphNode = RootAnimationNode | GroupAnimationNode | AnimateAnimationNode | MotionAnimationNode;

export type AnimationTarget =
  | { kind: "sceneNode"; nodeId: string }
  | { kind: "vectorVertex"; nodeId: string; vertexId: string }
  | { kind: "vectorHandle"; nodeId: string; edgeId: string; endpoint: "from" | "to" }
  | { kind: "vectorEdge"; nodeId: string; edgeId: string }
  | { kind: "animationNode"; nodeId: string };

export type VectorSubElementAnimationTarget = Extract<
  AnimationTarget,
  { kind: "vectorVertex" | "vectorHandle" | "vectorEdge" }
>;

export function isVectorSubElementAnimationTarget(
  target: AnimationTarget | undefined,
): target is VectorSubElementAnimationTarget {
  return target?.kind === "vectorVertex" || target?.kind === "vectorHandle" || target?.kind === "vectorEdge";
}

/** Color space used when animating a fill, stroke, or effect color. */
export type ColorAnimationSpace = "oklch" | "hsl" | "rgb" | "hsb";

/** Which numeric fields of a shadow / blur effect can be animated. */
export type EffectAnimationField = "blur" | "spread" | "offsetX" | "offsetY" | "opacity";

export type AnimationGroupNumericOverride =
  | { kind: "distributed"; from: number; to: number }
  | { kind: "random"; from: number; to: number; seed: number }
  | { kind: "list"; values: string };

export type AnimationPropertyGroupOverrides = Record<string, AnimationGroupNumericOverride>;

export type AnimationProperty = (
  | { id: string; kind: "opacity"; value: number }
  | { id: string; kind: "pin"; anchor: AnimationAnchor; x: number; y: number; target?: AnimationPinTarget; dependencyTarget?: AnimationTarget }
  | { id: string; kind: "followPath"; anchor: AnimationAnchor; progress: number; orientToPath?: boolean; pathTarget?: AnimationTarget }
  | { id: string; kind: "strokePath"; inverse: boolean; erase: boolean; progress: number }
  | { id: string; kind: "orbit"; angle: number; target?: AnimationTarget }
  | { id: string; kind: "lookAt"; target?: AnimationTarget }
  | { id: string; kind: "scale"; value: number }
  | { id: string; kind: "scaleX"; value: number }
  | { id: string; kind: "scaleY"; value: number }
  | { id: string; kind: "rotation"; value: number }
  | { id: string; kind: "width"; value: number }
  | { id: string; kind: "height"; value: number }
  | { id: string; kind: "layout"; axis: "x" | "y"; gap: number }
  | { id: string; kind: "stack"; gap: number }
  | { id: string; kind: "inline"; gap: number }
  | { id: string; kind: "fitToBBox"; pad: number; target?: AnimationTarget }
  | { id: string; kind: "prop"; name: string; value: number }
  /** Animate one or more channels of a fill paint color. fillIndex is 0-based. */
  | { id: string; kind: "fillColor"; fillIndex: number; colorSpace: ColorAnimationSpace; channels: string[]; color: OklchColor }
  /** Animate one or more channels of a stroke paint color. strokeIndex is 0-based. */
  | { id: string; kind: "strokeColor"; strokeIndex: number; colorSpace: ColorAnimationSpace; channels: string[]; color: OklchColor }
  /** Animate a numeric field of an effect (blur, spread, offsets). effectIndex is 0-based. */
  | { id: string; kind: "effectValue"; effectIndex: number; field: EffectAnimationField; value: number }
  /** Animate one or more channels of an effect color (drop/inner shadow color). effectIndex is 0-based. */
  | { id: string; kind: "effectColor"; effectIndex: number; colorSpace: ColorAnimationSpace; channels: string[]; color: OklchColor }
) & { groupOverrides?: AnimationPropertyGroupOverrides };

export type AnimationPinTarget =
  | { kind: "frameAnchor"; anchor: AnimationAnchor }
  | { kind: "pathPercent"; progress: number }
  | { kind: "distance"; distance: number }
  | { kind: "minDistance"; distance: number; snapBack?: boolean }
  | { kind: "maxDistance"; distance: number; snapBack?: boolean }
  | { kind: "offset" };

export type AnimationDistancePinTarget = Extract<
  AnimationPinTarget,
  { kind: "distance" | "minDistance" | "maxDistance" }
>;

export type AnimationClampedDistancePinTarget = Extract<
  AnimationPinTarget,
  { kind: "minDistance" | "maxDistance" }
>;

export function isDistancePinTarget(
  target: AnimationPinTarget | undefined,
): target is AnimationDistancePinTarget {
  return target?.kind === "distance" || target?.kind === "minDistance" || target?.kind === "maxDistance";
}

export function isClampedDistancePinTarget(
  target: AnimationPinTarget | undefined,
): target is AnimationClampedDistancePinTarget {
  return target?.kind === "minDistance" || target?.kind === "maxDistance";
}

/** When true, min/max distance is evaluated only at the current time (objects return
 *  once the limit is no longer violated). When false, the constraint is integrated
 *  along the dependency's travel so far, so displacements persist after a flyby. */
export function distancePinSnapBack(target: AnimationClampedDistancePinTarget): boolean {
  return target.snapBack === true;
}

export type AnimationAnchor =
  | "origin"
  | "topLeft"
  | "top"
  | "topRight"
  | "left"
  | "center"
  | "right"
  | "bottomLeft"
  | "bottom"
  | "bottomRight"
  | "baselineLeft"
  | "baseline";

const ANIMATION_ANCHORS: AnimationAnchor[] = [
  "origin",
  "topLeft",
  "top",
  "topRight",
  "left",
  "center",
  "right",
  "bottomLeft",
  "bottom",
  "bottomRight",
  "baselineLeft",
  "baseline",
];

export type AnimationPinKind =
  | "input"
  | "output"
  | "objectInput"
  | "dependencyInput"
  | "motionInput"
  | "motionOutput"
  | "motionSequenceInput"
  | "motionSequenceOutput";

export interface AnimationEndpoint {
  nodeId: string;
  pin: AnimationPinKind;
  index?: number;
}

export interface AnimationConnection {
  id: string;
  from: AnimationEndpoint;
  to: AnimationEndpoint;
}

export const ANIMATION_EASING_NAMES: AnimationEasingName[] = [
  "linear",
  "easeIn",
  "easeOut",
  "easeInOut",
  "easeCirc",
  "easeBack",
  "easeBounce",
  "easeElastic",
  "overShoot",
];

export const DEFAULT_WIGGLE_EASING: AnimationWiggleEasing = {
  kind: "wiggle",
  frequency: 3,
  phase: 0,
  amplitude: 1,
  decay: 0,
};

export function createEmptyAnimationGraph(): AnimationGraph {
  return { nodes: [], connections: [], visualGroups: [] };
}

export function normalizeAnimationGraph(graph: Partial<AnimationGraph> | undefined): AnimationGraph {
  const nodes = Array.isArray(graph?.nodes) ? graph.nodes.map(normalizeAnimationNode).filter(Boolean) as AnimationGraphNode[] : [];
  const nodeIds = new Set(nodes.map(node => node.id));
  const connections = Array.isArray(graph?.connections)
    ? graph.connections.filter(connection => (
      typeof connection.id === "string" &&
      nodeIds.has(connection.from?.nodeId) &&
      nodeIds.has(connection.to?.nodeId)
    )).map(connection => structuredClone(connection))
    : [];
  const visualGroups = normalizeAnimationVisualGroups(graph, nodeIds);
  return { nodes, connections, visualGroups };
}

export function createAnimationGraphNode(kind: AnimationNodeKind, position: AnimationGraphPoint, options: { frameId?: string } = {}): AnimationGraphNode {
  const id = createId("anim");
  switch (kind) {
    case "animation":
      return {
        id,
        kind,
        name: "Animation",
        position,
        frameId: options.frameId,
      };
    case "group":
      return {
        id,
        kind,
        name: "Group",
        position,
        targets: [],
        regroup: "none",
      };
    case "animate":
      return {
        id,
        kind,
        name: "Behavior",
        position,
        properties: [{ id: createId("animProp"), kind: "opacity", value: 1 }],
      };
    case "motion":
      return {
        id,
        kind,
        name: "Motion",
        position,
        durationMs: 400,
        instant: false,
        offsetMs: 0,
        stutterMs: 0,
        animateElementsIndependently: false,
        easing: { kind: "preset", name: "easeInOut" },
      };
  }
}

export function isAnimationNodeCollapsed(node: AnimationGraphNode): boolean {
  return node.kind !== "animation" && node.collapsed === true;
}

function normalizeAnimationNode(node: AnimationGraphNode): AnimationGraphNode | undefined {
  if (!node || typeof node.id !== "string" || typeof node.kind !== "string") {
    return undefined;
  }
  const collapsed = node.kind !== "animation" && node.collapsed === true;
  const base = {
    id: node.id,
    kind: node.kind,
    name: typeof node.name === "string" && node.name.length > 0 ? node.name : titleForKind(node.kind),
    position: normalizePoint(node.position),
    ...(collapsed ? { collapsed: true as const } : {}),
  };
  switch (node.kind) {
    case "animation":
      return { ...base, kind: "animation", frameId: typeof node.frameId === "string" ? node.frameId : undefined };
    case "group":
      return {
        ...base,
        kind: "group",
        targets: Array.isArray(node.targets) ? node.targets.map(target => structuredClone(target)) : [],
        regroup: normalizeAnimationGroupRegroup((node as Partial<GroupAnimationNode>).regroup),
        regroupOrder: Array.isArray((node as Partial<GroupAnimationNode>).regroupOrder)
          ? (node as Partial<GroupAnimationNode>).regroupOrder?.filter(id => typeof id === "string")
          : undefined,
      };
    case "animate":
      return {
        ...base,
        kind: "animate",
        target: normalizeAnimationTarget(node.target as AnimationTarget | undefined),
        properties: Array.isArray(node.properties)
          ? node.properties.map(normalizeAnimationProperty)
          : [{ id: createId("animProp"), kind: "opacity", value: 1 }],
      };
    case "motion":
      return {
        ...base,
        kind: "motion",
        durationMs: finiteNonNegative(node.durationMs, 400),
        instant: Boolean(node.instant),
        offsetMs: Number.isFinite(node.offsetMs) ? node.offsetMs : 0,
        stutterMs: finiteNonNegative((node as Partial<MotionAnimationNode>).stutterMs, 0),
        animateElementsIndependently: Boolean((node as Partial<MotionAnimationNode>).animateElementsIndependently),
        easing: normalizeAnimationEasing(node.easing),
        transition: Boolean(node.transition),
        targetAnimationNodeId: typeof node.targetAnimationNodeId === "string" ? node.targetAnimationNodeId : undefined,
        transitionEffect: normalizeAnimationTransitionEffect(node.transitionEffect),
        transitionColor: normalizeOklchColor(node.transitionColor, DEFAULT_TRANSITION_COLOR),
      };
    default:
      return undefined;
  }
}

function normalizeAnimationEasing(easing: unknown): AnimationEasing {
  if (!easing || typeof easing !== "object") {
    return { kind: "preset", name: "easeInOut" };
  }
  const raw = easing as Partial<AnimationEasing>;
  if (raw.kind === "wiggle") {
    const wiggle = raw as Partial<AnimationWiggleEasing>;
    return {
      kind: "wiggle",
      frequency: finiteNumber(wiggle.frequency, DEFAULT_WIGGLE_EASING.frequency),
      phase: finiteNumber(wiggle.phase, DEFAULT_WIGGLE_EASING.phase),
      amplitude: finiteNumber(wiggle.amplitude, DEFAULT_WIGGLE_EASING.amplitude),
      decay: finiteNumber(wiggle.decay, DEFAULT_WIGGLE_EASING.decay),
    };
  }
  const preset = raw as Partial<AnimationPresetEasing>;
  return {
    kind: "preset",
    name: ANIMATION_EASING_NAMES.includes(preset.name as AnimationEasingName) ? preset.name as AnimationEasingName : "easeInOut",
  };
}

function normalizeAnimationTransitionEffect(effect: unknown): AnimationTransitionEffectName {
  if (effect === "none") return "cut";
  if (effect === "dissolve") return "crossfade";
  if (effect === "flyInLeft") return "slideRight";
  if (effect === "flyInRight") return "slideLeft";
  if (effect === "flyInTop") return "slideDown";
  if (effect === "flyInBottom") return "slideUp";
  if (effect === "zoomIn") return "zoom";
  return ANIMATION_TRANSITION_EFFECTS.some(option => option.name === effect)
    ? effect as AnimationTransitionEffectName
    : "cut";
}

function normalizeAnimationGroupRegroup(regroup: unknown): AnimationGroupRegroupMode {
  return regroup === "words" || regroup === "symbols" || regroup === "symbolsWithoutWhitespace"
    ? regroup
    : "none";
}

function normalizeOklchColor(color: unknown, fallback: OklchColor = { l: 0.7, c: 0.15, h: 250, alpha: 1 }): OklchColor {
  if (!color || typeof color !== "object") {
    return { ...fallback };
  }
  const raw = color as Partial<OklchColor>;
  return {
    l: finiteNumber(raw.l, fallback.l),
    c: finiteNumber(raw.c, fallback.c),
    h: finiteNumber(raw.h, fallback.h),
    alpha: finiteNumber(raw.alpha, fallback.alpha),
  };
}

function normalizeAnimationVisualGroups(graph: Partial<AnimationGraph> | undefined, nodeIds: Set<string>): AnimationGraphVisualGroup[] {
  const groups = Array.isArray(graph?.visualGroups) ? graph.visualGroups : [];
  const seenGroupIds = new Set<string>();
  return groups.flatMap(group => {
    if (!group || typeof group.id !== "string" || seenGroupIds.has(group.id)) {
      return [];
    }
    const memberIds = Array.isArray(group.nodeIds)
      ? [...new Set(group.nodeIds.filter(nodeId => typeof nodeId === "string" && nodeIds.has(nodeId)))]
      : [];
    if (memberIds.length === 0) {
      return [];
    }
    seenGroupIds.add(group.id);
    return [{
      id: group.id,
      name: typeof group.name === "string" && group.name.trim().length > 0 ? group.name : "Group",
      nodeIds: memberIds,
      collapsed: Boolean(group.collapsed),
    }];
  });
}

const VALID_COLOR_SPACES: ColorAnimationSpace[] = ["oklch", "hsl", "rgb", "hsb"];
const VALID_EFFECT_FIELDS: EffectAnimationField[] = ["blur", "spread", "offsetX", "offsetY", "opacity"];

function normalizeAnimationProperty(property: AnimationProperty): AnimationProperty {
  property = withNormalizedGroupOverrides(property);
  const id = property.id || createId("animProp");
  if (property.kind === "pin") {
    const rawTarget = property.target as AnimationPinTarget | AnimationTarget | undefined;
    const target = rawTarget?.kind === "frameAnchor" && ANIMATION_ANCHORS.includes(rawTarget.anchor)
      ? { kind: "frameAnchor" as const, anchor: rawTarget.anchor }
      : rawTarget?.kind === "pathPercent"
        ? { kind: "pathPercent" as const, progress: normalizeAnimationProgress(rawTarget.progress) }
        : rawTarget?.kind === "distance"
          ? { kind: "distance" as const, distance: Math.max(0, finiteNumber(rawTarget.distance, 0)) }
        : rawTarget?.kind === "minDistance" || rawTarget?.kind === "maxDistance"
          ? {
              kind: rawTarget.kind,
              distance: Math.max(0, finiteNumber(rawTarget.distance, 0)),
              ...(rawTarget.snapBack === true ? { snapBack: true as const } : {}),
            }
        : rawTarget?.kind === "offset"
          ? { kind: "offset" as const }
      : undefined;
    const dependencyTarget = normalizeAnimationTarget(property.dependencyTarget ?? (
      rawTarget?.kind !== "frameAnchor"
      && rawTarget?.kind !== "pathPercent"
      && rawTarget?.kind !== "distance"
      && rawTarget?.kind !== "minDistance"
      && rawTarget?.kind !== "maxDistance"
      && rawTarget?.kind !== "offset"
        ? rawTarget
        : undefined
    ));
    return {
      ...property,
      id,
      anchor: ANIMATION_ANCHORS.includes(property.anchor) ? property.anchor : "center",
      x: finiteNumber(property.x, 0),
      y: finiteNumber(property.y, 0),
      target,
      dependencyTarget,
    };
  }
  if (property.kind === "followPath") {
    return {
      ...property,
      id,
      anchor: ANIMATION_ANCHORS.includes(property.anchor) ? property.anchor : "center",
      progress: normalizeAnimationProgress(property.progress),
      orientToPath: Boolean(property.orientToPath),
      pathTarget: normalizeAnimationTarget(property.pathTarget),
    };
  }
  if (property.kind === "strokePath") {
    return {
      ...property,
      id,
      inverse: Boolean(property.inverse),
      erase: Boolean(property.erase),
      progress: normalizeAnimationProgress(property.progress),
    };
  }
  if (property.kind === "orbit") {
    return {
      ...property,
      id,
      angle: finiteNumber(property.angle, 360),
      target: normalizeAnimationTarget(property.target),
    };
  }
  if (property.kind === "lookAt") {
    return {
      ...property,
      id,
      target: normalizeAnimationTarget(property.target),
    };
  }
  if (property.kind === "opacity") {
    const value = finiteNumber(property.value, 1);
    return { ...property, id, value: Math.max(0, Math.min(1, value > 1 ? value / 100 : value)) };
  }
  if (property.kind === "scale" || property.kind === "scaleX" || property.kind === "scaleY") {
    return { ...property, id, value: finiteNumber(property.value, 1) };
  }
  if (property.kind === "rotation" || property.kind === "width" || property.kind === "height" || property.kind === "prop") {
    return { ...property, id, value: finiteNumber(property.value, 0) };
  }
  if (property.kind === "fillColor") {
    return {
      ...property,
      id,
      fillIndex: Math.max(0, Math.round(finiteNumber(property.fillIndex, 0))),
      colorSpace: VALID_COLOR_SPACES.includes(property.colorSpace) ? property.colorSpace : "oklch",
      channels: Array.isArray(property.channels) && property.channels.length > 0 ? property.channels : ["l", "c", "h"],
      color: normalizeOklchColor(property.color),
    };
  }
  if (property.kind === "strokeColor") {
    return {
      ...property,
      id,
      strokeIndex: Math.max(0, Math.round(finiteNumber(property.strokeIndex, 0))),
      colorSpace: VALID_COLOR_SPACES.includes(property.colorSpace) ? property.colorSpace : "oklch",
      channels: Array.isArray(property.channels) && property.channels.length > 0 ? property.channels : ["l", "c", "h"],
      color: normalizeOklchColor(property.color),
    };
  }
  if (property.kind === "effectValue") {
    return {
      ...property,
      id,
      effectIndex: Math.max(0, Math.round(finiteNumber(property.effectIndex, 0))),
      field: VALID_EFFECT_FIELDS.includes(property.field) ? property.field : "blur",
      value: finiteNumber(property.value, 0),
    };
  }
  if (property.kind === "effectColor") {
    return {
      ...property,
      id,
      effectIndex: Math.max(0, Math.round(finiteNumber(property.effectIndex, 0))),
      colorSpace: VALID_COLOR_SPACES.includes(property.colorSpace) ? property.colorSpace : "oklch",
      channels: Array.isArray(property.channels) && property.channels.length > 0 ? property.channels : ["l", "c", "h"],
      color: normalizeOklchColor(property.color),
    };
  }
  return { ...property, id };
}

function withNormalizedGroupOverrides<T extends AnimationProperty>(property: T): T {
  const groupOverrides = normalizeGroupOverrides(property.groupOverrides);
  if (Object.keys(groupOverrides).length === 0) {
    const { groupOverrides: _groupOverrides, ...rest } = property;
    return rest as T;
  }
  return { ...property, groupOverrides } as T;
}

function normalizeGroupOverrides(overrides: unknown): AnimationPropertyGroupOverrides {
  if (!overrides || typeof overrides !== "object") {
    return {};
  }
  const normalized: AnimationPropertyGroupOverrides = {};
  for (const [field, override] of Object.entries(overrides)) {
    if (!field || !override || typeof override !== "object") {
      continue;
    }
    const raw = override as Partial<AnimationGroupNumericOverride>;
    if (raw.kind === "distributed") {
      normalized[field] = {
        kind: "distributed",
        from: finiteNumber(raw.from, 0),
        to: finiteNumber(raw.to, 0),
      };
    } else if (raw.kind === "random") {
      normalized[field] = {
        kind: "random",
        from: finiteNumber(raw.from, 0),
        to: finiteNumber(raw.to, 0),
        seed: Math.round(finiteNumber(raw.seed, 42)),
      };
    } else if (raw.kind === "list") {
      normalized[field] = {
        kind: "list",
        values: typeof raw.values === "string" ? raw.values : "",
      };
    }
  }
  return normalized;
}

export function normalizeAnimationTarget(target: AnimationTarget | undefined): AnimationTarget | undefined {
  if (!target) {
    return undefined;
  }
  if (target.kind === "sceneNode") {
    return typeof target.nodeId === "string" ? { kind: "sceneNode", nodeId: target.nodeId } : undefined;
  }
  if (target.kind === "animationNode") {
    return typeof target.nodeId === "string" ? { kind: "animationNode", nodeId: target.nodeId } : undefined;
  }
  if (target.kind === "vectorVertex") {
    return typeof target.nodeId === "string" && typeof target.vertexId === "string"
      ? { kind: "vectorVertex", nodeId: target.nodeId, vertexId: target.vertexId }
      : undefined;
  }
  if (target.kind === "vectorHandle") {
    return typeof target.nodeId === "string" && typeof target.edgeId === "string" && (target.endpoint === "from" || target.endpoint === "to")
      ? { kind: "vectorHandle", nodeId: target.nodeId, edgeId: target.edgeId, endpoint: target.endpoint }
      : undefined;
  }
  if (target.kind === "vectorEdge") {
    return typeof target.nodeId === "string" && target.nodeId.length > 0 && typeof target.edgeId === "string" && target.edgeId.length > 0
      ? { kind: "vectorEdge", nodeId: target.nodeId, edgeId: target.edgeId }
      : undefined;
  }
  return undefined;
}

function normalizePoint(point: AnimationGraphPoint | undefined): AnimationGraphPoint {
  return {
    x: finiteNumber(point?.x, 0),
    y: finiteNumber(point?.y, 0),
  };
}

function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function finiteNonNegative(value: unknown, fallback: number): number {
  return Math.max(0, finiteNumber(value, fallback));
}

function normalizeAnimationProgress(value: unknown): number {
  return Math.max(0, Math.min(1, finiteNumber(value, 1)));
}

function titleForKind(kind: string): string {
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}
